import { OTHER_REJECTION_REASON_VALUE } from 'src/constants/document-review/constants'
import type {
  ApproveMcDocumentPayload,
  ApproveReviewStepPayload,
  DocumentReviewStep,
  McReviewFormValues,
  RecollectReviewStepPayload,
  RejectMcDocumentPayload,
  ReviewFormValues,
} from 'src/interfaces/documentReview'
import { blankToUndefined } from 'src/utils/blankToUndefined'
import { isTemporaryDriverLicense } from 'src/utils/documentReviewForm'

interface ContractorPayloadInput {
  step: DocumentReviewStep
  values: ReviewFormValues
  reviewedByUser: string
}

interface RecollectPayloadInput extends ContractorPayloadInput {
  /** A rejection-reason id, or `OTHER_REJECTION_REASON_VALUE` for free text. */
  reasonId: string
  /** The chosen reason's `message`. Empty when the reviewer chose Other. */
  reasonMessage: string
  reasonDetails: string
}

interface McPayloadInput {
  partnerId: string
  partnerCollectedDataId: string
  values: McReviewFormValues
  reviewedByUser: string
}

interface RejectMcPayloadInput extends McPayloadInput {
  declineReason: string
}

/**
 * `expiresAt` is the one field sent as `null` rather than omitted when blank: the backend treats
 * an absent key as "leave it alone" and an explicit null as "clear it", and clearing is what a
 * reviewer means by emptying the field. Every other optional uses `blankToUndefined` so the key
 * is absent instead of present-and-empty.
 *
 * A temporary driver license also sends the contractor's address, which the backend writes back
 * to the contractor (street 2 as `''` when blank). A standard license never carries the address
 * keys at all.
 */
export const buildApproveStepPayload = ({
  step,
  values,
  reviewedByUser,
}: ContractorPayloadInput): ApproveReviewStepPayload => {
  const { partnerDriver } = step.partnerDriverCollectedData
  const addressMetadata = isTemporaryDriverLicense(step)
    ? {
        addressStreet1: blankToUndefined(values.addressStreet1),
        // Sent even when blank: the backend skips undefined keys when it writes the address back,
        // so `''` is the only way a cleared street 2 reaches the contractor (Retool sends it too).
        addressStreet2: values.addressStreet2.trim(),
        addressCity: blankToUndefined(values.addressCity),
        addressState: blankToUndefined(values.addressState),
        addressPostalCode: blankToUndefined(values.addressPostalCode),
      }
    : {}

  return {
    stepId: step.id,
    partnerDriverId: partnerDriver.id,
    partnerId: partnerDriver.partner.id,
    stepMetadata: {
      dateIssued: blankToUndefined(values.dateIssued),
      effectiveAt: blankToUndefined(values.effectiveAt),
      expiresAt: values.expiresAt.trim() === '' ? null : values.expiresAt.trim(),
      firstName: blankToUndefined(values.firstName),
      lastName: blankToUndefined(values.lastName),
      documentId: blankToUndefined(values.documentId),
      reviewedByUser,
      ...addressMetadata,
    },
  }
}

export const buildRecollectStepPayload = ({
  step,
  values,
  reviewedByUser,
  reasonId,
  reasonMessage,
  reasonDetails,
}: RecollectPayloadInput): RecollectReviewStepPayload => {
  const { partnerDriver } = step.partnerDriverCollectedData
  const isOther = reasonId === OTHER_REJECTION_REASON_VALUE

  return {
    stepId: step.id,
    partnerDriverId: partnerDriver.id,
    partnerId: partnerDriver.partner.id,
    stepMetadata: {
      dateIssued: blankToUndefined(values.dateIssued),
      expiresAt: values.expiresAt.trim() === '' ? null : values.expiresAt.trim(),
      firstName: blankToUndefined(values.firstName),
      lastName: blankToUndefined(values.lastName),
      documentId: blankToUndefined(values.documentId),
      reviewedByUser,
      // Other is a UI-only option, so it has no id to send — the `partner-review-rejection-reason`
      // table has no such row.
      partnerReviewRejectionReasonId: isOther ? undefined : reasonId,
      // The contractor sees this string, so Other sends the reviewer's own words.
      declineReason: isOther ? blankToUndefined(reasonDetails) : blankToUndefined(reasonMessage),
      declineReasonDetails: isOther ? blankToUndefined(reasonDetails) : undefined,
    },
  }
}

/**
 * `reviewMetadata`, NOT `stepMetadata` — `PartnerApproveDocumentDto` and `PartnerRejectDocumentDto`
 * both destructure `reviewMetadata`, and the Notion spec's `stepMetadata` is wrong (divergence #2).
 *
 * No `firstName` / `lastName`: `PartnerCollectedDataReviewMetadataDto` does not declare them, so
 * Retool's habit of sending them wrote nowhere (divergence #3).
 */
export const buildApproveMcDocumentPayload = ({
  partnerId,
  partnerCollectedDataId,
  values,
  reviewedByUser,
}: McPayloadInput): ApproveMcDocumentPayload => {
  const expiresAt = values.expiresAt.trim()

  if (expiresAt === '') {
    // Required and @IsNotEmpty on approve. resolveMcApproveBlockReason blocks submission before a
    // caller reaches this point, so a blank value here means that gate was skipped — fail loudly
    // rather than build a request the backend would 400 on anyway.
    throw new Error('buildApproveMcDocumentPayload requires a non-blank expiresAt')
  }

  return {
    partnerId,
    partnerCollectedDataId,
    reviewMetadata: {
      dateIssued: blankToUndefined(values.dateIssued),
      expiresAt,
      documentId: blankToUndefined(values.documentId),
      reviewedByUser,
    },
  }
}

export const buildRejectMcDocumentPayload = ({
  partnerId,
  partnerCollectedDataId,
  values,
  reviewedByUser,
  declineReason,
}: RejectMcPayloadInput): RejectMcDocumentPayload => {
  return {
    partnerId,
    partnerCollectedDataId,
    reviewMetadata: {
      dateIssued: blankToUndefined(values.dateIssued),
      expiresAt: blankToUndefined(values.expiresAt),
      documentId: blankToUndefined(values.documentId),
      reviewedByUser,
      declineReason,
    },
  }
}
